/**
 * DGConfigModal.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * SELF-CONTAINED SNAPSHOT DESIGN
 *
 * Problem with old approach:
 *   dg_config only stored { groups: { [id]: { enabled, attrs, lovOverrides:{} } } }
 *   GovernanceUI needed to JOIN against master config at runtime to get LOV data.
 *   Master config is large and shouldn't be a runtime dependency per application.
 *
 * New approach — "Snapshot on Apply":
 *   When the user clicks "Apply & Save Snapshot", we write a COMPLETE self-contained
 *   object into dg_config:
 *     • Every group's enabled flag
 *     • Every attribute's enabled flag
 *     • Every attribute's FULL LOV array (master default OR user-edited override)
 *     • Full taxonomy hierarchy
 *     • Full anonymization scoring model (factors + thresholds)
 *     • Full CDE scoring config (weights + thresholds + d1_rules + d5_rules)
 *
 *   GovernanceUI reads ONLY from dg_config.  Master config is NEVER needed again
 *   after the application snapshot is created.
 *
 * Backward-compat:
 *   mergeSnapshotWithMaster() handles old saved configs that lack LOV data by
 *   backfilling from the current master. User-customised LOVs (lovCustomized map)
 *   are always preserved.
 *
 * Props:
 *   config     — existing saved DGAppConfig (null = new application)
 *   onApply    — receives complete self-contained DGAppConfig snapshot
 *   show/onHide
 *   app_nm / entity — optional context labels for job re-run flows
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { adriva_getConfig } from "../API";
import {
  LovEditorModal,
  type TaxonomyLevelConfig,
  DEFAULT_TAXONOMY_CONFIG,
} from "./DGConfigLovEditorModal";

import BusinessTermConfigModal, {
  type BusinessTermRunConfig,
  BTMode,
} from "./BusinessTermConfigModal";

// ═══════════════════════════════════════════════════════════════════════════════
// EXPORTED TYPES  (consumed by GovernanceUI, DataInputPage, FieldGovernanceEditor)
// ═══════════════════════════════════════════════════════════════════════════════

export interface LovItem {
  id: string;
  label: string;
  description?: string;
  score?: number | null;
  jurisdiction?: string;
  full_title?: string;
  reversible?: boolean;
}

export interface TaxonomyEntry {
  id?: string;
  l1: string;
  l2: string;
  l3: string;
}

export interface Attribute {
  id: string;
  label: string;
  type: string;
  mandatory?: boolean;
  enabled_by_default?: boolean;
  description?: string;
  lov: LovItem[] | null;
}

export interface EnrichmentGroup {
  id: string;
  label: string;
  subtitle?: string;
  description?: string;
  icon?: string;
  accent: string;
  agent: string;
  phase: string;
  attributes: Attribute[];
}

export interface ScoringFactor {
  id: string;
  label: string;
  weight: number;
  description: string;
}

export interface ScoringThreshold {
  recommendation: string;
  min_score: number;
  max_score: number;
  description: string;
}

export interface CDEScoringWeights {
  d1: number; d2: number; d3: number; d4: number; d5: number;
}

export interface CDEScoringThresholds {
  cde_critical: number;
  cde_significant: number;
  non_cde_important: number;
}

export interface CDEScoringConfig {
  weights: CDEScoringWeights;
  thresholds: CDEScoringThresholds;
  /** Preserved from master — rules engine uses these, stored in snapshot */
  d1_rules?: Record<string, unknown>;
  d5_rules?: Record<string, unknown>;
}

/**
 * GroupSnapshot — fully resolved state for one enrichment group.
 *
 * `lovs`          — AUTHORITATIVE LOV map. Initialised from master on first save.
 *                   Replaced by user edits. Consumed directly by GovernanceUI.
 * `lovCustomized` — tracks which attrIds have user edits (for UI diff badges + revert).
 */
export interface GroupSnapshot {
  enabled: boolean;
  attrs: Record<string, { enabled: boolean }>;
  lovs: Record<string, LovItem[]>;           // full resolved LOVs for every attr
  lovCustomized: Record<string, boolean>;    // true = user override, false/absent = master default
}

/**
 * BusinessTermModeConfig — stored inside DGAppConfig.
 * mode:  "generate"  → LLM creates terms freely
 *        "excel"     → mapped to terms imported from CSV/Excel
 *        "collibra"  → mapped to terms pulled from Collibra
 *        "alation"   → mapped to terms pulled from Alation
 * terms: the imported terms (only populated for non-generate modes)
 */
export interface BusinessTermModeConfig {
  mode:              "generate" | "excel" | "collibra" | "alation";
  terms:             Array<{ id: string; term: string; description?: string; domain?: string }>;
  catalog_connected: boolean;
  source_meta?:      Record<string, any>;
}

export const DEFAULT_BT_CONFIG: BusinessTermModeConfig = {
  mode:              "generate",
  terms:             [],
  catalog_connected: false,
};

/**
 * DGAppConfig — complete self-contained governance config for one application.
 * No master dependency after snapshot is created.
 */
export interface DGAppConfig {
  groups: Record<string, GroupSnapshot>;
  scoringModel: {
    factors: ScoringFactor[];
    thresholds: ScoringThreshold[];
  };
  taxonomyLevelConfig: TaxonomyLevelConfig;
  taxonomyHierarchy: TaxonomyEntry[];       // full hierarchy snapshot
  cdeScoringConfig: CDEScoringConfig;
  businessTermConfig: BusinessTermModeConfig; // how to handle business terms in this run
}

/** Raw shape from adriva_getConfig({ configType: 'DG_SETUP_CONFIG' }) */
export interface DGMasterConfig {
  _meta?: Record<string, string>;
  enrichment_groups: EnrichmentGroup[];
  anonymization_scoring_model: {
    label: string;
    description: string;
    factors: ScoringFactor[];
    thresholds: ScoringThreshold[];
  };
  taxonomy: {
    label: string;
    description: string;
    id_scheme?: string;
    hierarchy: TaxonomyEntry[];
  };
  cde_scoring: {
    _comment?: string;
    weights: Record<string, unknown>;
    thresholds: Record<string, unknown>;
    d1_rules?: Record<string, unknown>;
    d5_rules?: Record<string, unknown>;
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// SNAPSHOT BUILDERS  (exported — reusable in tests / server-side)
// ═══════════════════════════════════════════════════════════════════════════════

function cleanNumericRecord(raw: Record<string, unknown>): Record<string, number> {
  return Object.fromEntries(
    Object.entries(raw)
      .filter(([k, v]) => !k.startsWith("_") && typeof v === "number")
      .map(([k, v]) => [k, v as number])
  );
}

/**
 * buildFullSnapshot
 * Converts DGMasterConfig → fully resolved DGAppConfig.
 * Every LOV array is deep-copied — no master references survive.
 */
export function buildFullSnapshot(master: DGMasterConfig): DGAppConfig {
  const groups: Record<string, GroupSnapshot> = {};

  for (const g of master.enrichment_groups) {
    const lovs: Record<string, LovItem[]> = {};
    const attrs: Record<string, { enabled: boolean }> = {};

    for (const a of g.attributes) {
      attrs[a.id] = { enabled: a.enabled_by_default !== false };
      if (a.lov && a.lov.length > 0) {
        lovs[a.id] = a.lov.map((item) => ({ ...item }));
      }
    }

    groups[g.id] = { enabled: true, attrs, lovs, lovCustomized: {} };
  }

  const rawW = cleanNumericRecord(master.cde_scoring.weights as Record<string, unknown>);
  const rawT = cleanNumericRecord(master.cde_scoring.thresholds as Record<string, unknown>);

  return {
    groups,
    scoringModel: {
      factors:    master.anonymization_scoring_model.factors.map((f) => ({ ...f })),
      thresholds: master.anonymization_scoring_model.thresholds.map((t) => ({ ...t })),
    },
    taxonomyLevelConfig: DEFAULT_TAXONOMY_CONFIG,
    taxonomyHierarchy:   master.taxonomy.hierarchy.map((t) => ({ ...t })),
    cdeScoringConfig: {
      weights:    rawW as unknown as CDEScoringWeights,
      thresholds: rawT as unknown as CDEScoringThresholds,
      d1_rules:   master.cde_scoring.d1_rules ? JSON.parse(JSON.stringify(master.cde_scoring.d1_rules)) : undefined,
      d5_rules:   master.cde_scoring.d5_rules ? JSON.parse(JSON.stringify(master.cde_scoring.d5_rules)) : undefined,
    },
    businessTermConfig: { ...DEFAULT_BT_CONFIG },
  };
}

/**
 * mergeSnapshotWithMaster
 * Opens an existing saved DGAppConfig onto a fresh master.
 *
 * Strategy:
 *   • enabled states → preserved from saved config
 *   • LOVs with lovCustomized[attrId]=true → keep saved LOV
 *   • LOVs without customisation → use fresh master LOV
 *   • New groups/attrs from master → added with defaults
 *   • scoringModel + cdeScoringConfig → keep saved versions
 *   • taxonomyHierarchy → keep saved if taxonomy LOV was customised
 *
 * Also handles backward-compat: if saved config has no `lovs` field (old format),
 * master LOVs are backfilled automatically.
 */
export function mergeSnapshotWithMaster(saved: DGAppConfig, master: DGMasterConfig): DGAppConfig {
  const fresh = buildFullSnapshot(master);

  for (const g of master.enrichment_groups) {
    const sg = saved.groups?.[g.id];
    if (!sg) continue;

    fresh.groups[g.id].enabled = sg.enabled;

    for (const a of g.attributes) {
      if (sg.attrs?.[a.id] !== undefined) {
        fresh.groups[g.id].attrs[a.id] = { ...sg.attrs[a.id] };
      }
      // Preserve user-customised LOV (or backfill from saved lovs if present but not flagged)
      const hasCustomFlag = sg.lovCustomized?.[a.id] === true;
      const hasSavedLovs  = sg.lovs?.[a.id]?.length > 0;

      if (hasCustomFlag && hasSavedLovs) {
        fresh.groups[g.id].lovs[a.id]          = sg.lovs[a.id].map((x) => ({ ...x }));
        fresh.groups[g.id].lovCustomized[a.id]  = true;
      }
      // Otherwise fresh master LOV stays (already populated by buildFullSnapshot)
    }
  }

  fresh.scoringModel        = saved.scoringModel        ?? fresh.scoringModel;
  fresh.cdeScoringConfig    = saved.cdeScoringConfig    ?? fresh.cdeScoringConfig;
  fresh.taxonomyLevelConfig = saved.taxonomyLevelConfig ?? DEFAULT_TAXONOMY_CONFIG;

  // Keep saved taxonomy if user customised the taxonomy LOV
  const taxCustomised = Object.values(saved.groups ?? {}).some(
    (g) => g.lovCustomized?.["data_type_taxonomy"]
  );
  if (taxCustomised && saved.taxonomyHierarchy?.length > 0) {
    fresh.taxonomyHierarchy = saved.taxonomyHierarchy.map((t) => ({ ...t }));
  }

  // Preserve businessTermConfig — user's term mode choice is always kept
  fresh.businessTermConfig = saved.businessTermConfig
    ? { ...DEFAULT_BT_CONFIG, ...saved.businessTermConfig }
    : { ...DEFAULT_BT_CONFIG };

  return fresh;
}

/**
 * getEffectiveLov — runtime helper for GovernanceUI.
 * Returns the full LOV for an attribute directly from dg_config.
 * No master config needed.
 */
export function getEffectiveLov(groupId: string, attrId: string, config: DGAppConfig): LovItem[] | null {
  return config?.groups?.[groupId]?.lovs?.[attrId] ?? null;
}

/**
 * enrichmentGroupsFromSnapshot — reconstruct enrichment_groups for GovernanceUI.
 * Filters disabled groups/attrs and substitutes snapshot LOVs.
 * Requires structural metadata (label, icon, etc.) from masterGroups.
 */
export function enrichmentGroupsFromSnapshot(
  masterGroups: EnrichmentGroup[],
  config: DGAppConfig
): EnrichmentGroup[] {
  return masterGroups
    .filter((g) => config.groups?.[g.id]?.enabled !== false)
    .map((g) => ({
      ...g,
      attributes: g.attributes
        .filter((a) => config.groups?.[g.id]?.attrs?.[a.id]?.enabled !== false)
        .map((a) => ({
          ...a,
          lov: config.groups?.[g.id]?.lovs?.[a.id] ?? a.lov,
        })),
    }));
}

// ═══════════════════════════════════════════════════════════════════════════════
// UI PRIMITIVE — Toggle
// ═══════════════════════════════════════════════════════════════════════════════

const Toggle: React.FC<{
  checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; size?: "sm" | "md";
}> = ({ checked, onChange, disabled, size = "md" }) => {
  const w = size === "sm" ? 32 : 38, h = size === "sm" ? 18 : 22, th = size === "sm" ? 12 : 16;
  return (
    <label style={{ position: "relative", width: w, height: h, flexShrink: 0, cursor: disabled ? "not-allowed" : "pointer", display: "inline-block" }}>
      <input type="checkbox" checked={checked} disabled={disabled}
        onChange={(e) => !disabled && onChange(e.target.checked)}
        style={{ opacity: 0, width: 0, height: 0, position: "absolute" }} />
      <span style={{ position: "absolute", inset: 0, borderRadius: 99, background: checked ? "#3b82f6" : "#cbd5e1", transition: "background 0.18s" }} />
      <span style={{ position: "absolute", top: (h - th) / 2, left: checked ? w - th - 3 : 3, width: th, height: th, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.15)", transition: "left 0.18s" }} />
    </label>
  );
};

const typeColor = (type: string) => ({
  lov_single: { bg: "#eff6ff", text: "#2563eb" }, lov_multi: { bg: "#eff6ff", text: "#2563eb" },
  boolean: { bg: "#f0fdf4", text: "#16a34a" }, computed_score: { bg: "#fefce8", text: "#ca8a04" },
  taxonomy: { bg: "#fdf4ff", text: "#9333ea" },
}[type] ?? { bg: "#f8fafc", text: "#64748b" });

const typeLabel = (type: string) => ({
  lov_single: "Single Select", lov_multi: "Multi Select", boolean: "Yes / No",
  computed_score: "Computed Score", taxonomy: "Taxonomy", free_text: "Free Text",
}[type] ?? type);

// ═══════════════════════════════════════════════════════════════════════════════
// ANONYMIZATION SCORING EDITOR
// ═══════════════════════════════════════════════════════════════════════════════

const ScoringModelEditor: React.FC<{
  model: DGAppConfig["scoringModel"]; onChange: (m: DGAppConfig["scoringModel"]) => void;
}> = ({ model, onChange }) => {
  const total   = model.factors.reduce((s, f) => s + Number(f.weight), 0);
  const isValid = Math.abs(total - 1.0) < 0.005;
  return (
    <div style={{ marginTop: 10, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ padding: "9px 14px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>Anonymization Scoring — Factor Weights</span>
        <span style={{ fontSize: 11, color: isValid ? "#16a34a" : "#dc2626" }}>Total: {total.toFixed(2)} {isValid ? "✓" : "⚠ must sum to 1.00"}</span>
      </div>
      <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
        {model.factors.map((f, idx) => (
          <div key={f.id}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: "#0f172a", minWidth: 210 }}>{f.label}</span>
              <input type="number" min={0} max={1} step={0.01} value={f.weight}
                onChange={(e) => {
                  const factors = model.factors.map((x, i) => i === idx ? { ...x, weight: parseFloat(e.target.value) || 0 } : x);
                  onChange({ ...model, factors });
                }}
                style={{ width: 60, textAlign: "center", fontSize: 13, fontWeight: 700, border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 6px", outline: "none" }} />
              <div style={{ flex: 1, height: 6, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.min((f.weight / 0.5) * 100, 100)}%`, background: "linear-gradient(90deg,#3b82f6,#8b5cf6)", borderRadius: 99, transition: "width 0.3s" }} />
              </div>
              <span style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, minWidth: 36, textAlign: "right" }}>{(f.weight * 100).toFixed(0)}%</span>
            </div>
            <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2, paddingLeft: 220 }}>{f.description}</div>
          </div>
        ))}
      </div>
      <div style={{ padding: "10px 14px", borderTop: "1px solid #e2e8f0", background: "#fafbfc" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8 }}>Score Thresholds</div>
        {model.thresholds.map((t, idx) => (
          <div key={t.recommendation} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", minWidth: 160 }}>{t.recommendation}</span>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>min score</span>
            <input type="number" min={0} max={5} step={0.1} value={t.min_score}
              onChange={(e) => {
                const thresholds = model.thresholds.map((x, i) => i === idx ? { ...x, min_score: parseFloat(e.target.value) || 0 } : x);
                onChange({ ...model, thresholds });
              }}
              style={{ width: 55, textAlign: "center", fontSize: 13, fontWeight: 700, border: "1px solid #e2e8f0", borderRadius: 6, padding: "3px 5px", outline: "none" }} />
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CDE SCORING EDITOR
// ═══════════════════════════════════════════════════════════════════════════════

const DIM_LABELS: Record<string, string> = {
  d1: "D1 — Regulatory Obligation",        d2: "D2 — Business Decision Impact",
  d3: "D3 — Data Quality Consequence",     d4: "D4 — Downstream Dependency Breadth",
  d5: "D5 — Identity & Reference Integrity",
};
const THRESH_META: Record<string, { label: string; color: string }> = {
  cde_critical:      { label: "CDE Critical ≥",      color: "#dc2626" },
  cde_significant:   { label: "CDE Significant ≥",   color: "#ea580c" },
  non_cde_important: { label: "Non-CDE Important ≥", color: "#d97706" },
};

const CDEScoringEditor: React.FC<{ config: CDEScoringConfig; onChange: (c: CDEScoringConfig) => void }> = ({ config, onChange }) => {
  const total   = Object.values(config.weights).reduce((s, v) => s + Number(v), 0);
  const isValid = Math.abs(total - 1.0) < 0.005;
  return (
    <div style={{ border: "1px solid #fecaca", borderRadius: 8, overflow: "hidden", marginTop: 6 }}>
      <div style={{ padding: "9px 14px", background: "#fef2f2", borderBottom: "1px solid #fecaca", display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#991b1b", textTransform: "uppercase", letterSpacing: "0.8px" }}>CDE Scoring — Dimension Weights</span>
        <span style={{ fontSize: 11, color: isValid ? "#16a34a" : "#dc2626" }}>Total: {total.toFixed(2)} {isValid ? "✓" : "⚠ must sum to 1.00"}</span>
      </div>
      <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
        {(Object.keys(config.weights) as Array<keyof CDEScoringWeights>).map((key) => (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: "#0f172a", minWidth: 280 }}>{DIM_LABELS[key] ?? key}</span>
            <input type="number" min={0} max={1} step={0.01} value={config.weights[key]}
              onChange={(e) => onChange({ ...config, weights: { ...config.weights, [key]: parseFloat(e.target.value) || 0 } })}
              style={{ width: 60, textAlign: "center", fontSize: 13, fontWeight: 700, border: "1px solid #fecaca", borderRadius: 6, padding: "4px 6px", outline: "none" }} />
            <div style={{ flex: 1, height: 6, background: "#fee2e2", borderRadius: 99, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.min((config.weights[key] / 0.5) * 100, 100)}%`, background: "linear-gradient(90deg,#ef4444,#991b1b)", borderRadius: 99 }} />
            </div>
            <span style={{ fontSize: 11, color: "#dc2626", fontWeight: 600, minWidth: 36, textAlign: "right" }}>{(config.weights[key] * 100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
      <div style={{ padding: "10px 14px", borderTop: "1px solid #fecaca", background: "#fff5f5" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#991b1b", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8 }}>Classification Thresholds (0.0–3.0)</div>
        {(Object.keys(config.thresholds) as Array<keyof CDEScoringThresholds>).map((key) => {
          const m = THRESH_META[key] ?? { label: key, color: "#374151" };
          return (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", borderRadius: 7, border: "1px solid #fee2e2", background: "#fff", marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: m.color, minWidth: 200 }}>{m.label}</span>
              <input type="number" min={0} max={3} step={0.1} value={config.thresholds[key]}
                onChange={(e) => onChange({ ...config, thresholds: { ...config.thresholds, [key]: parseFloat(e.target.value) || 0 } })}
                style={{ width: 60, textAlign: "center", fontSize: 13, fontWeight: 700, border: `1px solid ${m.color}44`, borderRadius: 6, padding: "3px 5px", outline: "none", color: m.color }} />
              <div style={{ flex: 1, height: 5, background: "#fee2e2", borderRadius: 99, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.min((config.thresholds[key] / 3) * 100, 100)}%`, background: m.color, borderRadius: 99 }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ATTRIBUTE ROW
// ═══════════════════════════════════════════════════════════════════════════════

const AttributeRow: React.FC<{
  attr: Attribute;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  currentLov: LovItem[] | null;     // snapshot LOV (already resolved from master or user edit)
  masterLov: LovItem[] | null;      // original master LOV — for diff display only
  isCustomized: boolean;
  onLovSave: (lov: LovItem[]) => void;
  onLovRevert: () => void;
  scoringModel: DGAppConfig["scoringModel"];
  onScoringModelChange: (m: DGAppConfig["scoringModel"]) => void;
  taxonomyHierarchy: TaxonomyEntry[];
  taxonomyLevelConfig: TaxonomyLevelConfig;
  onTaxonomyLevelConfigChange: (cfg: TaxonomyLevelConfig) => void;
  // Business Term Source button — only active on business_element_name attr
  btConfig?:    BusinessTermModeConfig;
  onBtConfig?:  (cfg: BusinessTermModeConfig) => void;
  appNm?:       string;
  entityId?:    string;
}> = ({
  attr, enabled, onToggle, currentLov, masterLov, isCustomized,
  onLovSave, onLovRevert, scoringModel, onScoringModelChange,
  taxonomyHierarchy, taxonomyLevelConfig, onTaxonomyLevelConfigChange,
  btConfig, onBtConfig, appNm = "", entityId = "",
}) => {
  const [lovOpen,     setLovOpen]     = useState(false);
  const [scoringOpen, setScoringOpen] = useState(false);
  const [btModalOpen, setBtModalOpen] = useState(false);
  const isBTAttr   = attr.id === "business_element_name";
  const isTaxonomy = attr.type === "taxonomy";
  const hasLov     = (attr.lov !== null || isTaxonomy) && !["free_text", "boolean", "computed_score"].includes(attr.type);
  const isScoring  = attr.id === "anonymization_score";
  const tc         = typeColor(attr.type);

  const masterForEditor: LovItem[] = isTaxonomy
    ? taxonomyHierarchy.map((t) => ({ id: t.id ?? [t.l1, t.l2, t.l3].join("__"), label: t.l3 || t.l2 || t.l1, description: `${t.l1} › ${t.l2} › ${t.l3}` }))
    : (masterLov ?? []);

  const lovCount = (currentLov ?? masterForEditor).length;

  return (
    <div style={{ padding: "14px 0", paddingLeft: 20, borderBottom: "1px solid #f8fafc", opacity: enabled ? 1 : 0.45 }}>
      <div style={{ display: "grid", gridTemplateColumns: "38px 1fr auto auto", gap: 10, alignItems: "start" }}>
        <div style={{ paddingTop: 2 }}><Toggle checked={enabled} onChange={onToggle} disabled={attr.mandatory} /></div>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 3 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a" }}>{attr.label}</span>
            {attr.mandatory && <span style={{ fontSize: 9.5, fontWeight: 700, padding: "1px 6px", borderRadius: 3, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", textTransform: "uppercase" }}>required</span>}
            {isCustomized && <span style={{ fontSize: 9.5, fontWeight: 600, padding: "1px 6px", borderRadius: 3, background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" }}>✎ customized</span>}
            {isTaxonomy && (["useL1", "useL2", "useL3"] as const).map((k, i) => (
              <span key={k} onClick={() => onTaxonomyLevelConfigChange({ ...taxonomyLevelConfig, [k]: !taxonomyLevelConfig[k] })}
                style={{ cursor: "pointer", fontSize: 10, padding: "1px 7px", borderRadius: 4, border: "1px solid", userSelect: "none",
                  background: taxonomyLevelConfig[k] ? "#eff6ff" : "#f8fafc",
                  color:      taxonomyLevelConfig[k] ? "#2563eb" : "#94a3b8",
                  borderColor:taxonomyLevelConfig[k] ? "#bfdbfe" : "#e2e8f0" }}>L{i + 1}</span>
            ))}
          </div>
          <p style={{ fontSize: 12, color: "#64748b", lineHeight: 1.55, margin: 0 }}>{attr.description}</p>

          {enabled && hasLov && (
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              <button onClick={() => setLovOpen(true)}
                style={{ all: "unset", boxSizing: "border-box", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 11px", borderRadius: 5, border: "1px solid #e2e8f0", background: isCustomized ? "#fffbeb" : "#f8fafc", fontSize: 12, color: isCustomized ? "#d97706" : "#64748b", fontWeight: 500 }}>
                {isTaxonomy ? "🗂 Edit Taxonomy Values" : "✎ Edit Allowed Values"}
                <span style={{ fontSize: 10.5, color: "#94a3b8" }}>({lovCount})</span>
                {isCustomized && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#d97706" }} />}
              </button>
              {isCustomized && !isTaxonomy && (
                <button onClick={onLovRevert}
                  style={{ all: "unset", boxSizing: "border-box", cursor: "pointer", display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: 5, border: "1px solid #fecaca", background: "#fef2f2", fontSize: 11.5, color: "#dc2626" }}>
                  ↺ Revert to Master
                </button>
              )}
            </div>
          )}
          {enabled && isScoring && (
            <>
              <button onClick={() => setScoringOpen((o) => !o)}
                style={{ all: "unset", boxSizing: "border-box", cursor: "pointer", marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 11px", borderRadius: 5, border: `1px solid ${scoringOpen ? "#bfdbfe" : "#e2e8f0"}`, background: scoringOpen ? "#eff6ff" : "#f8fafc", fontSize: 12, color: scoringOpen ? "#2563eb" : "#64748b", fontWeight: 500 }}>
                {scoringOpen ? "▴ Hide scoring model" : "▾ Edit scoring model"}
              </button>
              {scoringOpen && <ScoringModelEditor model={scoringModel} onChange={onScoringModelChange} />}
            </>
          )}

          {/* ── Business Term Source button — ONLY for business_element_name ── */}
          {enabled && isBTAttr && onBtConfig && (
            <div style={{ marginTop: 10 }}>
              <button
                onClick={() => setBtModalOpen(true)}
                style={{
                  all: "unset", boxSizing: "border-box", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "6px 14px", borderRadius: 6, fontSize: 12.5, fontWeight: 600,
                  border: `1.5px solid ${(btConfig?.mode ?? "generate") !== "generate" ? "#6ee7b7" : "#c7d2fe"}`,
                  background: (btConfig?.mode ?? "generate") !== "generate" ? "#f0fdf4" : "#eef2ff",
                  color:      (btConfig?.mode ?? "generate") !== "generate" ? "#065f46"  : "#3730a3",
                  transition: "all 0.12s",
                }}
              >
                <span>📚</span>
                <span>Business Term Source</span>
                {(btConfig?.mode ?? "generate") === "generate" ? (
                  <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3,
                    background: "#e0e7ff", color: "#4338ca", fontWeight: 700 }}>
                    🤖 AI Generate
                  </span>
                ) : (
                  <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3,
                    background: "#dcfce7", color: "#166534", fontWeight: 700 }}>
                    {(btConfig?.terms?.length ?? 0)} terms · {btConfig?.mode}
                  </span>
                )}
                <span style={{ fontSize: 10, color: "#9ca3af" }}>⚙ Configure →</span>
              </button>

              {btModalOpen && (
                <BusinessTermConfigModal
                  show={btModalOpen}
                  onHide={() => setBtModalOpen(false)}
                  appNm={appNm}
                  entity={entityId}
                  entityId={entityId}
                  config={{
                    // Pass existing mode + terms through directly — modal now uses same BTMode values
                    mode:          (btConfig?.mode ?? "generate") as BTMode,
                    terms:         (btConfig?.terms ?? []) as any,
                    glossary_count: btConfig?.terms?.length ?? 0,
                    entity_filter: entityId,
                  }}
                  onChange={(runCfg) => {
                    if (!onBtConfig) return;
                    // Bridge: BusinessTermRunConfig → BusinessTermModeConfig
                    onBtConfig({
                      mode:              runCfg.mode,
                      terms:             runCfg.terms ?? [],
                      catalog_connected: runCfg.mode !== "generate",
                    } as BusinessTermModeConfig);
                  }}
                  onSave={(runCfg) => {
                    if (!onBtConfig) return;
                    // Full bridge including imported terms[]
                    onBtConfig({
                      mode:              runCfg.mode,
                      terms:             runCfg.terms ?? [],
                      catalog_connected: runCfg.mode !== "generate",
                      source_meta:       { glossary_count: runCfg.glossary_count, entity_filter: runCfg.entity_filter },
                    } as BusinessTermModeConfig);
                    setBtModalOpen(false);
                  }}
                />
              )}
            </div>
          )}
        </div>
        <span style={{ fontSize: 10.5, fontWeight: 600, padding: "3px 9px", borderRadius: 5, background: tc.bg, color: tc.text, whiteSpace: "nowrap", marginTop: 2 }}>{typeLabel(attr.type)}</span>
        <div style={{ paddingTop: 2 }}><Toggle checked={enabled} onChange={onToggle} disabled={attr.mandatory} /></div>
      </div>

      {hasLov && (
        <LovEditorModal
          show={lovOpen}
          onHide={() => setLovOpen(false)}
          attribute={attr}
          masterLov={masterForEditor}
          customLov={isCustomized ? (currentLov ?? null) : null}
          onSave={(lov: LovItem[]) => { onLovSave(lov); setLovOpen(false); }}
          taxonomyHierarchy={taxonomyHierarchy}
          taxonomyLevelConfig={taxonomyLevelConfig}
          onTaxonomyLevelConfigChange={onTaxonomyLevelConfigChange}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// GROUP CARD
// ═══════════════════════════════════════════════════════════════════════════════

const GroupCard: React.FC<{
  group: EnrichmentGroup;
  snapshot: GroupSnapshot;
  masterLovs: Record<string, LovItem[]>;
  onGroupToggle: (v: boolean) => void;
  onAttrToggle: (aid: string, v: boolean) => void;
  onLovSave: (aid: string, lov: LovItem[]) => void;
  onLovRevert: (aid: string) => void;
  scoringModel: DGAppConfig["scoringModel"];
  onScoringModelChange: (m: DGAppConfig["scoringModel"]) => void;
  cdeScoringConfig: CDEScoringConfig;
  onCDEScoringChange: (c: CDEScoringConfig) => void;
  taxonomyHierarchy: TaxonomyEntry[];
  taxonomyLevelConfig: TaxonomyLevelConfig;
  onTaxonomyLevelConfigChange: (cfg: TaxonomyLevelConfig) => void;
  // Business Term passthrough — only used by data_source_element_dictionary group
  btConfig?:    BusinessTermModeConfig;
  onBtConfig?:  (cfg: BusinessTermModeConfig) => void;
  appNm?:       string;
  entityId?:    string;
}> = ({ group, snapshot, masterLovs, onGroupToggle, onAttrToggle, onLovSave, onLovRevert,
        scoringModel, onScoringModelChange, cdeScoringConfig, onCDEScoringChange,
        taxonomyHierarchy, taxonomyLevelConfig, onTaxonomyLevelConfigChange,
        btConfig, onBtConfig, appNm = "", entityId = "" }) => {
  const [open, setOpen] = useState(false);
  const [cdeOpen, setCdeOpen] = useState(false);
  const isCDE        = group.id === "cde_assessment";
  const customCount  = Object.values(snapshot.lovCustomized ?? {}).filter(Boolean).length;
  const enabledCount = group.attributes.filter((a) => snapshot.attrs[a.id]?.enabled !== false).length;

  const agentBadge = ({ metadata: { bg:"#ede9fe", color:"#6d28d9", border:"#ddd6fe", label:"🧠 Metadata" },
    privacy: { bg:"#fce7f3", color:"#be185d", border:"#fbcfe8", label:"🔒 Privacy" },
    cde:     { bg:"#fee2e2", color:"#991b1b", border:"#fecaca", label:"🔴 CDE" } }[group.agent]
    ?? { bg:"#f1f5f9", color:"#475569", border:"#e2e8f0", label:group.agent });

  return (
    <div style={{ border: "1px solid", borderRadius: 12, overflow: "hidden", marginBottom: 12, background: "#fff",
      borderColor: snapshot.enabled ? (isCDE ? "#fca5a5" : "#e2e8f0") : "#f1f5f9",
      boxShadow: open ? "0 2px 12px rgba(0,0,0,0.06)" : "none" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 18px", cursor: "pointer",
          background: open ? (isCDE ? "#fff5f5" : "#fafbff") : "#fff", borderBottom: open ? "1px solid #f1f5f9" : "none" }}
        onClick={() => setOpen((o) => !o)}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: group.accent + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{group.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: snapshot.enabled ? "#0f172a" : "#94a3b8" }}>{group.label}</span>
            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, background: agentBadge.bg, color: agentBadge.color, border: `1px solid ${agentBadge.border}` }}>{agentBadge.label}</span>
            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd" }}>{group.phase}</span>
            {snapshot.enabled && group.attributes.length > 0 && (
              <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0" }}>{enabledCount}/{group.attributes.length} attrs</span>
            )}
            {customCount > 0 && <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" }}>✎ {customCount} customized</span>}
          </div>
          <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>{group.subtitle}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <Toggle checked={snapshot.enabled} onChange={onGroupToggle} />
          <span style={{ color: "#94a3b8", fontSize: 12, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
        </div>
      </div>

      {open && snapshot.enabled && (
        <div style={{ padding: "0 18px 12px" }}>
          {group.description && (
            <p style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.65, margin: "12px 0 10px", padding: "10px 14px", background: "#f8fafc", borderRadius: 8, borderLeft: `3px solid ${group.accent}` }}>{group.description}</p>
          )}
          {isCDE ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.8px" }}>CDE Scoring Configuration</span>
                <button onClick={() => setCdeOpen((o) => !o)}
                  style={{ all: "unset", boxSizing: "border-box", cursor: "pointer", fontSize: 11.5, color: "#64748b", padding: "4px 12px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                  {cdeOpen ? "▴ Hide" : "▾ Configure weights & thresholds"}
                </button>
              </div>
              {cdeOpen
                ? <CDEScoringEditor config={cdeScoringConfig} onChange={onCDEScoringChange} />
                : <div style={{ padding: "12px 14px", border: "1px solid #fecaca", borderRadius: 8, background: "#fff5f5", fontSize: 12.5, color: "#991b1b", lineHeight: 1.6 }}>
                    CDE Assessment scores fields across 5 dimensions (D1–D5). Weights and thresholds are stored in your application snapshot.
                  </div>
              }
            </div>
          ) : group.attributes.length > 0 && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0 4px" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.8px" }}>Attributes</span>
                <div style={{ display: "flex", gap: 6 }}>
                  {[{ label: "Enable all", v: true }, { label: "Disable all", v: false }].map(({ label, v }) => (
                    <button key={label} onClick={() => group.attributes.forEach((a) => !a.mandatory && onAttrToggle(a.id, v))}
                      style={{ all: "unset", boxSizing: "border-box", cursor: "pointer", fontSize: 11.5, color: "#64748b", padding: "3px 10px", borderRadius: 5, border: "1px solid #e2e8f0", background: "#f8fafc" }}>{label}</button>
                  ))}
                </div>
              </div>
              {group.attributes.map((attr) => (
                <AttributeRow key={attr.id}
                  attr={attr}
                  enabled={snapshot.attrs[attr.id]?.enabled !== false}
                  onToggle={(v) => onAttrToggle(attr.id, v)}
                  currentLov={snapshot.lovs?.[attr.id] ?? null}
                  masterLov={masterLovs[attr.id] ?? null}
                  isCustomized={!!snapshot.lovCustomized?.[attr.id]}
                  onLovSave={(lov) => onLovSave(attr.id, lov)}
                  onLovRevert={() => onLovRevert(attr.id)}
                  scoringModel={scoringModel}
                  onScoringModelChange={onScoringModelChange}
                  taxonomyHierarchy={taxonomyHierarchy}
                  taxonomyLevelConfig={taxonomyLevelConfig}
                  onTaxonomyLevelConfigChange={onTaxonomyLevelConfigChange}
                  btConfig={attr.id === "business_element_name" ? btConfig : undefined}
                  onBtConfig={attr.id === "business_element_name" ? onBtConfig : undefined}
                  appNm={appNm}
                  entityId={entityId}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DGConfigContent — modal body (also usable standalone)
// ═══════════════════════════════════════════════════════════════════════════════

export interface DGConfigContentProps {
  draft: DGAppConfig;
  masterGroups: EnrichmentGroup[];
  masterLovsByGroup: Record<string, Record<string, LovItem[]>>;
  onChange: (config: DGAppConfig) => void;
  appNm?:   string;
  entityId?: string;
}

export const DGConfigContent: React.FC<DGConfigContentProps> = ({
  draft, masterGroups, masterLovsByGroup, onChange,
  appNm = "", entityId = "",
}) => {
  const enabledGroups = Object.values(draft.groups).filter((g) => g.enabled).length;
  const totalCustom   = Object.values(draft.groups).reduce((s, g) => s + Object.values(g.lovCustomized ?? {}).filter(Boolean).length, 0);
  const anonTotal     = draft.scoringModel.factors.reduce((s, f) => s + Number(f.weight), 0);
  const anonValid     = Math.abs(anonTotal - 1.0) < 0.005;
  const cdeTotal      = Object.values(draft.cdeScoringConfig.weights).reduce((s, v) => s + Number(v), 0);
  const cdeValid      = Math.abs(cdeTotal - 1.0) < 0.005;
  const btMode        = draft.businessTermConfig?.mode ?? "generate";
  const btTermCount   = draft.businessTermConfig?.terms?.length ?? 0;

  // Helpers that produce immutable updates

  const setGroupEnabled = (gid: string, v: boolean) =>
    onChange({ ...draft, groups: { ...draft.groups, [gid]: { ...draft.groups[gid], enabled: v } } });

  const setAttrEnabled = (gid: string, aid: string, v: boolean) =>
    onChange({ ...draft, groups: { ...draft.groups, [gid]: { ...draft.groups[gid], attrs: { ...draft.groups[gid].attrs, [aid]: { enabled: v } } } } });

  /**
   * saveLov — user saved a LOV edit.
   * • Replace lovs[aid] with the user's new full array → GovernanceUI reads this directly.
   * • Set lovCustomized[aid] = true → enables "✎ customized" badge + "Revert" button.
   */
  const saveLov = (gid: string, aid: string, lov: LovItem[]) =>
    onChange({ ...draft, groups: { ...draft.groups, [gid]: { ...draft.groups[gid],
      lovs:          { ...draft.groups[gid].lovs,          [aid]: lov },
      lovCustomized: { ...draft.groups[gid].lovCustomized,  [aid]: true },
    } } });

  /**
   * revertLov — user clicked "Revert to Master".
   * • Restore lovs[aid] from master → GovernanceUI gets the original LOV back.
   * • Clear lovCustomized[aid].
   */
  const revertLov = (gid: string, aid: string) => {
    const masterLov = masterLovsByGroup[gid]?.[aid] ?? [];
    onChange({ ...draft, groups: { ...draft.groups, [gid]: { ...draft.groups[gid],
      lovs:          { ...draft.groups[gid].lovs,          [aid]: masterLov.map((x) => ({ ...x })) },
      lovCustomized: { ...draft.groups[gid].lovCustomized,  [aid]: false },
    } } });
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      {/* Summary strip */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        {[
          { label: "Groups Enabled",  val: `${enabledGroups}/${masterGroups.length}`, color: "#4f46e5" },
          { label: "Custom LOVs",     val: totalCustom,       color: "#d97706" },
          { label: "Anon Weight",     val: anonTotal.toFixed(2), color: anonValid ? "#059669" : "#dc2626" },
          { label: "CDE Weight",      val: cdeTotal.toFixed(2),  color: cdeValid  ? "#059669" : "#dc2626" },
          {
            label: "Business Terms",
            val:   btMode === "generate" ? "AI" : `${btTermCount}`,
            color: btMode === "generate" ? "#7c3aed" : btTermCount > 0 ? "#059669" : "#d97706",
          },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ flex: "1 1 100px", padding: "10px 14px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0", textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color }}>{val}</div>
            <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "#eff6ff", border: "1px solid #bfdbfe", fontSize: 12.5, color: "#1e40af", lineHeight: 1.6 }}>
        ℹ️ <strong>Self-contained snapshot:</strong> All LOVs, taxonomy, scoring weights and CDE config are embedded
        directly in this application's config. GovernanceUI uses this snapshot — no master config dependency at runtime.
      </div>

      {(!anonValid || !cdeValid) && (
        <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", fontSize: 12.5, color: "#dc2626" }}>
          ⚠ Scoring weights must sum to 1.00. {!anonValid && `Anonymization: ${anonTotal.toFixed(2)}.`} {!cdeValid && `CDE: ${cdeTotal.toFixed(2)}.`}
        </div>
      )}

      {masterGroups.map((group) => (
        <GroupCard key={group.id}
          group={group}
          snapshot={draft.groups[group.id] ?? { enabled: true, attrs: {}, lovs: {}, lovCustomized: {} }}
          masterLovs={masterLovsByGroup[group.id] ?? {}}
          onGroupToggle={(v) => setGroupEnabled(group.id, v)}
          onAttrToggle={(aid, v) => setAttrEnabled(group.id, aid, v)}
          onLovSave={(aid, lov) => saveLov(group.id, aid, lov)}
          onLovRevert={(aid) => revertLov(group.id, aid)}
          scoringModel={draft.scoringModel}
          onScoringModelChange={(m) => onChange({ ...draft, scoringModel: m })}
          cdeScoringConfig={draft.cdeScoringConfig}
          onCDEScoringChange={(c) => onChange({ ...draft, cdeScoringConfig: c })}
          taxonomyHierarchy={draft.taxonomyHierarchy}
          taxonomyLevelConfig={draft.taxonomyLevelConfig}
          onTaxonomyLevelConfigChange={(cfg) => onChange({ ...draft, taxonomyLevelConfig: cfg })}
          btConfig={draft.businessTermConfig}
          onBtConfig={(cfg) => onChange({ ...draft, businessTermConfig: cfg })}
          appNm={appNm}
          entityId={entityId}
        />
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DGConfigModal — Bootstrap Modal shell
// ═══════════════════════════════════════════════════════════════════════════════

export interface DGConfigModalProps {
  config: DGAppConfig | null;
  onApply: (config: DGAppConfig) => void;
  show: boolean;
  onHide: () => void;
  app_nm?: string;
  entity?: string;
}

export const DGConfigModal: React.FC<DGConfigModalProps> = ({ config, onApply, show, onHide, app_nm, entity }) => {
  const [master,    setMaster]    = useState<DGMasterConfig | null>(null);
  const [loading,   setLoading]   = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft,     setDraft]     = useState<DGAppConfig | null>(null);
  const fetchedRef = useRef(false);

  /** masterLovsByGroup — used only for "Revert to Master" diffs. Never passed to GovernanceUI. */
  const masterLovsByGroup = useMemo(() => {
    if (!master) return {} as Record<string, Record<string, LovItem[]>>;
    const out: Record<string, Record<string, LovItem[]>> = {};
    for (const g of master.enrichment_groups) {
      out[g.id] = {};
      for (const a of g.attributes) {
        if (a.lov?.length) out[g.id][a.id] = a.lov.map((x) => ({ ...x }));
      }
    }
    return out;
  }, [master]);

  const fetchMaster = useCallback(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    setLoadError(null);
    adriva_getConfig(
      JSON.stringify({ configType: "DG_SETUP_CONFIG" }),
      (res: any) => {
        setLoading(false);
        if (res?.success && res?.data) {
          const m: DGMasterConfig = res.data;
          setMaster(m);
          setDraft(config ? mergeSnapshotWithMaster(config, m) : buildFullSnapshot(m));
        } else {
          setLoadError("Failed to load governance master config.");
          fetchedRef.current = false;
        }
      },
      (err: any) => {
        setLoading(false);
        setLoadError(err?.message ?? "Network error loading DG master config.");
        fetchedRef.current = false;
      }
    );
  }, [config]);

  useEffect(() => { if (show) { fetchedRef.current = false; fetchMaster(); } }, [show]);
  useEffect(() => { if (!show || !master) return; setDraft(config ? mergeSnapshotWithMaster(config, master) : buildFullSnapshot(master)); }, [config]);

  const handleApply = () => { if (draft) { onApply(draft); onHide(); } };

  const anonTotal = draft?.scoringModel.factors.reduce((s, f) => s + Number(f.weight), 0) ?? 0;
  const anonValid = Math.abs(anonTotal - 1.0) < 0.005;
  const cdeTotal  = draft ? Object.values(draft.cdeScoringConfig.weights).reduce((s, v) => s + Number(v), 0) : 0;
  const cdeValid  = Math.abs(cdeTotal - 1.0) < 0.005;
  const canApply  = !!draft && anonValid && cdeValid;

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "16px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>⚙️</div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1.2px" }}>
              Data Governance {app_nm && <span style={{ color: "#6366f1" }}>· {app_nm}{entity ? ` / ${entity}` : ""}</span>}
            </div>
            <Modal.Title style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", margin: 0 }}>Enrichment Configuration</Modal.Title>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: "20px 24px", background: "#f8fafc", minHeight: 300 }}>
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 60, gap: 16 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #bfdbfe", borderTopColor: "#3b82f6", animation: "dg-spin 0.8s linear infinite" }} />
            <span style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}>Loading governance master config…</span>
            <style>{`@keyframes dg-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        {loadError && !loading && (
          <div style={{ padding: "20px 24px", borderRadius: 10, background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: 13 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠ Failed to load master config</div>
            <div>{loadError}</div>
            <button onClick={() => { fetchedRef.current = false; fetchMaster(); }}
              style={{ all: "unset", boxSizing: "border-box", marginTop: 12, cursor: "pointer", padding: "7px 16px", borderRadius: 7, border: "1px solid #fecaca", background: "#fff", color: "#dc2626", fontSize: 13, fontWeight: 600 }}>↺ Retry</button>
          </div>
        )}
        {!loading && !loadError && draft && master && (
          <DGConfigContent
            draft={draft}
            masterGroups={master.enrichment_groups}
            masterLovsByGroup={masterLovsByGroup}
            onChange={setDraft}
            appNm={app_nm ?? ""}
            entityId={entity ?? ""}
          />
        )}
      </Modal.Body>

      <Modal.Footer style={{ background: "#fff", borderTop: "1px solid #e2e8f0", padding: "12px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={() => master && setDraft(buildFullSnapshot(master))} disabled={!master}
          style={{ all: "unset", boxSizing: "border-box", cursor: master ? "pointer" : "not-allowed", padding: "8px 16px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontSize: 13, fontWeight: 500, opacity: master ? 1 : 0.5 }}>
          ↺ Reset to Master Defaults
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onHide}
            style={{ all: "unset", boxSizing: "border-box", cursor: "pointer", padding: "8px 18px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontSize: 13, fontWeight: 500 }}>Cancel</button>
          <button onClick={handleApply} disabled={!canApply}
            style={{ all: "unset", boxSizing: "border-box", cursor: canApply ? "pointer" : "not-allowed", padding: "8px 22px", borderRadius: 7, background: canApply ? "#4f46e5" : "#e0e7ff", color: canApply ? "#fff" : "#a5b4fc", fontSize: 13, fontWeight: 600 }}>
            {canApply ? "✓ Apply & Save Snapshot" : "⚠ Fix weights first"}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};